import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MotionConfig, motion } from 'motion/react';
import { Check, Download, Loader2, PackageSearch } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  analyticsControllerGetOrdersDrilldownCustomers,
  analyticsControllerGetOrdersDrilldownOrders,
} from '@/generated/korza-api-client';
import type {
  OrdersDrilldownCustomersResponseDto,
  OrdersDrilldownOrdersResponseDto,
} from '@/generated/korza-api-client/types.gen';
import type { ChartGranularity } from '@/lib/chart-axis';
import {
  DRILLDOWN_PAGE_SIZE,
  DEFAULT_CUSTOMER_SORT,
  DEFAULT_ORDER_SORT,
  isCustomerSort,
  isOrderSort,
  isSameDrilldownPeriod,
  type DrilldownOrigin,
  resolveDrilldownSort,
  type CustomerSort,
  type DrilldownQuery,
  type DrilldownView,
  type OrderSort,
  type RepurchaseSegment,
} from '@/lib/orders-drilldown';
import { OrdersDrilldownCustomersTable } from '@/components/orders-drilldown/orders-drilldown-customers-table';
import { OrdersDrilldownStats } from '@/components/orders-drilldown/orders-drilldown-stats';
import { OrdersDrilldownPagination } from '@/components/orders-drilldown/orders-drilldown-pagination';
import { OrdersDrilldownToolbar } from '@/components/orders-drilldown/orders-drilldown-toolbar';
import { OrdersDrilldownOrdersTable } from '@/components/orders-drilldown/orders-drilldown-orders-table';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useOrdersDrilldownCsv } from '@/components/orders-drilldown/use-orders-drilldown-csv';
import { useOrdersDrilldownViewModel } from '@/components/orders-drilldown/use-orders-drilldown-view-model';

export interface OrdersDrilldownDialogProps {
  tenantSlug: string;
  /** Null means closed. */
  query: DrilldownQuery | null;
  /** What opened the drill-down (null while closed). */
  origin: DrilldownOrigin | null;
  granularity: ChartGranularity;
  /** Label of the filtered period, shown when the drill-down has no bucket. */
  periodLabel: string;
  onPageChange: (page: number) => void;
  onSortChange: (sort: OrderSort | CustomerSort) => void;
  onSegmentChange: (segment: RepurchaseSegment) => void;
  onViewChange: (view: DrilldownView) => void;
  onSearchChange: (search: string | null) => void;
  onClose: () => void;
}

export function OrdersDrilldownDialog({
  tenantSlug,
  query: currentQuery,
  origin: currentOrigin,
  granularity,
  periodLabel,
  onPageChange,
  onSortChange,
  onSegmentChange,
  onViewChange,
  onSearchChange,
  onClose,
}: OrdersDrilldownDialogProps) {
  const { LL } = useI18nContext();

  // On close the parent drops the query at once; keep rendering the last one
  // so the dialog fades out with its content instead of blanking first.
  const lastQuery = useRef<DrilldownQuery | null>(null);
  if (currentQuery !== null) lastQuery.current = currentQuery;
  const query = currentQuery ?? lastQuery.current;
  const lastOrigin = useRef<DrilldownOrigin | null>(null);
  if (currentOrigin !== null) lastOrigin.current = currentOrigin;
  const origin = currentOrigin ?? lastOrigin.current;

  const ordersQuery = useQuery<OrdersDrilldownOrdersResponseDto>({
    // Every request param is part of the key, so a stale response never
    // replaces the one for the latest click.
    queryKey: ['repurchaseDrilldownOrders', tenantSlug, query],
    queryFn: async () => {
      if (query?.view !== 'orders') throw new Error('Not the orders view');
      const res = await analyticsControllerGetOrdersDrilldownOrders({
        path: { tenantSlug },
        query,
        throwOnError: true,
      });
      return res.data;
    },
    enabled: query?.view === 'orders',
    // Keep the previous page on screen (dimmed) instead of flashing skeletons,
    // but only within the same period/bucket: a new filter never shows the
    // previous filter's numbers.
    placeholderData: (previousData, previousQuery) =>
      isSameDrilldownPeriod(
        previousQuery?.queryKey[2] as DrilldownQuery | undefined,
        query
      )
        ? previousData
        : undefined,
  });

  const customersQuery = useQuery<OrdersDrilldownCustomersResponseDto>({
    queryKey: ['repurchaseDrilldownCustomers', tenantSlug, query],
    queryFn: async () => {
      if (query?.view !== 'customers') {
        throw new Error('Not the customers view');
      }
      const res = await analyticsControllerGetOrdersDrilldownCustomers({
        path: { tenantSlug },
        query,
        throwOnError: true,
      });
      return res.data;
    },
    enabled: query?.view === 'customers',
    placeholderData: (previousData, previousQuery) =>
      isSameDrilldownPeriod(
        previousQuery?.queryKey[2] as DrilldownQuery | undefined,
        query
      )
        ? previousData
        : undefined,
  });

  const view = query?.view ?? 'orders';
  const activeQuery = view === 'orders' ? ordersQuery : customersQuery;
  const activeData = activeQuery.data;
  const summary = activeData?.summary;
  const search = query?.search ?? null;

  const csv = useOrdersDrilldownCsv({ tenantSlug, query, origin });
  const { title, stats, emptyText, isEmpty, canExport, isRepurchase } =
    useOrdersDrilldownViewModel({
      query,
      origin,
      granularity,
      periodLabel,
      summary,
      total: activeData?.total,
      isError: activeQuery.isError,
    });

  const sort = resolveDrilldownSort(view, query?.sort ?? null);
  const page = query?.page ?? 1;

  return (
    <Dialog
      open={currentQuery !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {/* Near full-screen so a page of rows fits without scrolling on desktop.
          The dialog itself never scrolls: header and footer stay put, and the
          body only scrolls as a fallback on short viewports. */}
      <DialogContent
        motion="smooth"
        overlayClassName="bg-black/60 backdrop-blur-sm data-[state=open]:duration-300 data-[state=closed]:[animation-duration:260ms]"
        className="flex h-[94vh] w-[96vw] max-w-[1600px] flex-col gap-0 overflow-hidden p-0 sm:rounded-xl"
      >
        <MotionConfig reducedMotion="user">
          <div className="relative flex shrink-0 flex-col gap-3 border-b border-border px-6 pb-3 pt-5">
            <DialogHeader className="flex-row items-start justify-between gap-4 space-y-0 pr-8">
              <div className="min-w-0 space-y-1">
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription className="sr-only">
                  {view === 'customers'
                    ? LL.metricsPage.repurchaseDrilldown.customersDescription()
                    : LL.metricsPage.repurchaseDrilldown.description()}
                </DialogDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                disabled={!canExport || csv.isLoading}
                onClick={() => void csv.download()}
              >
                {csv.isLoading ? (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                ) : csv.isDone ? (
                  <motion.span
                    initial={{ scale: 0, rotate: -90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 18 }}
                    className="mr-2 inline-flex text-green-500"
                  >
                    <Check className="size-4" />
                  </motion.span>
                ) : (
                  <Download className="mr-2 size-4" />
                )}
                {csv.isLoading
                  ? LL.metricsPage.repurchaseDrilldown.exportingCsv()
                  : csv.isDone
                    ? LL.metricsPage.repurchaseDrilldown.csvDone()
                    : LL.metricsPage.repurchaseDrilldown.exportCsv()}
              </Button>
            </DialogHeader>

            <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
              <OrdersDrilldownStats stats={stats} />
              {query !== null && origin !== null ? (
                <OrdersDrilldownToolbar
                  segment={origin}
                  isRepurchase={isRepurchase}
                  view={view}
                  search={search}
                  onSegmentChange={onSegmentChange}
                  onViewChange={onViewChange}
                  onSearchChange={onSearchChange}
                />
              ) : null}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6">
            {activeQuery.isError ? (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <p className="text-sm text-muted-foreground">
                  {view === 'customers'
                    ? LL.metricsPage.repurchaseDrilldown.customersError()
                    : LL.metricsPage.repurchaseDrilldown.error()}
                </p>
                <Button
                  variant="outline"
                  onClick={() => void activeQuery.refetch()}
                >
                  {LL.metricsPage.repurchaseDrilldown.retry()}
                </Button>
              </div>
            ) : isEmpty ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center gap-4 py-20 text-center"
              >
                <motion.div
                  animate={{ y: [0, -6, 0] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground"
                >
                  <PackageSearch className="size-7" aria-hidden="true" />
                </motion.div>
                <p className="max-w-sm text-sm text-muted-foreground">
                  {emptyText}
                </p>
              </motion.div>
            ) : view === 'customers' ? (
              <OrdersDrilldownCustomersTable
                customers={customersQuery.data?.items ?? []}
                isLoading={customersQuery.isLoading}
                isRefreshing={customersQuery.isPlaceholderData}
                sort={isCustomerSort(sort) ? sort : DEFAULT_CUSTOMER_SORT}
                onSortChange={onSortChange}
              />
            ) : (
              <OrdersDrilldownOrdersTable
                orders={ordersQuery.data?.items ?? []}
                isLoading={ordersQuery.isLoading}
                isRefreshing={ordersQuery.isPlaceholderData}
                isRepurchase={isRepurchase}
                sort={isOrderSort(sort) ? sort : DEFAULT_ORDER_SORT}
                onSortChange={onSortChange}
              />
            )}
          </div>

          {activeData !== undefined && activeData.total > 0 ? (
            <div className="shrink-0 border-t border-border px-6 py-3">
              <OrdersDrilldownPagination
                page={page}
                pageSize={DRILLDOWN_PAGE_SIZE}
                total={activeData.total}
                onPageChange={onPageChange}
              />
            </div>
          ) : null}
        </MotionConfig>
      </DialogContent>
    </Dialog>
  );
}
